import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';

/** One step of a dosing pump's OWN calibration, run on the board (backend R-337).
 *
 *  ⚠ **This is not the site-side throughput register next door.** That records a
 *  number we measured about a water pump; this asks a dosing pump to learn its
 *  mL/s and store it in its own NVS, and it RUNS THE PUMP to do it. Different
 *  permission (`actuator_control: write`, the same as any command that moves
 *  liquid), different endpoint, and it reaches hardware rather than a table.
 *
 *  Two steps, because a person stands between them: run the pump, weigh what came
 *  out, report the weight. The board pairs them — it remembers the run time and
 *  divides the reported volume by it. */
export interface PumpCalibrationStep {
  /** Plumbing room code. */
  room: string;
  /** Node code when the pump is on a child board; omit for the room's own board. */
  node?: string | null;
  /** `dosing_pump_a` … `dosing_pump_d`. */
  actuator: string;
  /** Step 1 — run this long (the backend bounds it to 10–120 s). */
  runSeconds?: number;
  /** Step 2 — the volume that run delivered, in millilitres. */
  measuredMl?: number;
}

export interface PumpCalibrationAccepted {
  id: string;
  parent_mac: string;
  status: string;
}

function path(s: PumpCalibrationStep): string {
  const base = `/v1/iot/plumbing-rooms/${encodeURIComponent(s.room)}`;
  const place = s.node ? `${base}/nodes/${encodeURIComponent(s.node)}` : base;
  return `${place}/actuators/${encodeURIComponent(s.actuator)}/calibration`;
}

export function useCalibratePump() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: PumpCalibrationStep) =>
      api.post<PumpCalibrationAccepted>(
        path(s),
        s.runSeconds !== undefined ? { run_seconds: s.runSeconds } : { measured_ml: s.measuredMl },
      ),
    // The new mL/s comes back through the INVENTORY, not in this response: the
    // board stores it and re-announces, so the figure appears after a poll rather
    // than immediately. Invalidating is what makes it show up without a reload.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['device-inventory-local'] });
    },
  });
}
